import { useEffect, useMemo, useRef, useState } from "react";

import { Button, ModalWrapper } from "../ui";
import CreateAnnouncementModal from "./CreateAnnouncementModal";
import {
  getUsersByRole,
  startImpersonation,
  stopImpersonation,
} from "../../services/api/core";
import useOutsideClick from "../../hooks/useOutsideClick";
import { openAdminPanel } from "../../utils/common";
import { getUserDisplayName, getUserInitials } from "../../utils/business";
import { LANGUAGE_CONFIG, UI_COPY } from "../../config";
import { useUiText } from "../../hooks/useUiText";

/**
 * Compact mouse-sized language control for the desktop portal.
 *
 * @param {Object} props Component props.
 * @param {string} props.language Active language code.
 * @param {boolean} props.disabled Whether language changes are in flight.
 * @param {Function} props.onChange Called with the next language code.
 * @param {Function} props.t Resolves bilingual copy.
 * @returns {JSX.Element} Desktop language toggle.
 */
const DesktopLanguageToggle = ({ language, disabled, onChange, t }) => (
  <div className="inline-flex overflow-hidden rounded-md border border-gray-300">
    <button
      className={`h-5 px-2.5 text-xs font-semibold transition ${
        language === LANGUAGE_CONFIG.ENGLISH
          ? "bg-primary text-white"
          : "bg-white text-gray-600 hover:bg-gray-50"
      }`}
      type="button"
      disabled={disabled}
      onClick={() => onChange(LANGUAGE_CONFIG.ENGLISH)}
    >
      {t(UI_COPY.nav.english)}
    </button>
    <button
      className={`h-5 px-2.5 text-xs font-semibold transition ${
        language === LANGUAGE_CONFIG.SPANISH
          ? "bg-primary text-white"
          : "bg-white text-gray-600 hover:bg-gray-50"
      }`}
      type="button"
      disabled={disabled}
      onClick={() => onChange(LANGUAGE_CONFIG.SPANISH)}
    >
      {t(UI_COPY.nav.spanish)}
    </button>
  </div>
);

/**
 * Finger-sized language control for the mobile portal sheet.
 *
 * @param {Object} props Component props.
 * @param {string} props.language Active language code.
 * @param {boolean} props.disabled Whether language changes are in flight.
 * @param {Function} props.onChange Called with the next language code.
 * @param {Function} props.t Resolves bilingual copy.
 * @returns {JSX.Element} Mobile language toggle.
 */
const MobileLanguageToggle = ({ language, disabled, onChange, t }) => (
  <div className="mt-3 grid grid-cols-2 gap-3">
    <button
      className={`min-h-[52px] touch-manipulation rounded-lg border-2 text-base font-semibold transition ${
        language === LANGUAGE_CONFIG.ENGLISH
          ? "border-primary bg-primary text-white"
          : "border-gray-300 bg-white text-gray-700"
      }`}
      type="button"
      disabled={disabled}
      onClick={() => onChange(LANGUAGE_CONFIG.ENGLISH)}
    >
      {t(UI_COPY.nav.english)}
    </button>
    <button
      className={`min-h-[52px] touch-manipulation rounded-lg border-2 text-base font-semibold transition ${
        language === LANGUAGE_CONFIG.SPANISH
          ? "border-primary bg-primary text-white"
          : "border-gray-300 bg-white text-gray-700"
      }`}
      type="button"
      disabled={disabled}
      onClick={() => onChange(LANGUAGE_CONFIG.SPANISH)}
    >
      {t(UI_COPY.nav.spanish)}
    </button>
  </div>
);

/**
 * User menu for language, admin access, announcements, impersonation, and logout.
 *
 * @param {Object} props Component props.
 * @param {Object|null} props.user Effective current user.
 * @param {Object} props.impersonation Current impersonation metadata.
 * @param {string|null} props.baseURL Backend base URL.
 * @param {Function} props.refreshUser Refreshes CoreContext user metadata.
 * @param {Function} props.updatePreferredLanguage Optimistically persists language.
 * @param {Function} props.onLogout Opens logout confirmation.
 * @returns {JSX.Element} User portal.
 */
const UserPortal = ({
  user,
  impersonation,
  baseURL,
  refreshUser,
  updatePreferredLanguage,
  onLogout,
}) => {
  const portalRef = useRef(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMenuRendered, setIsMenuRendered] = useState(false);
  const [isMenuShown, setIsMenuShown] = useState(false);
  const [isImpersonationModalOpen, setIsImpersonationModalOpen] = useState(false);
  const [isAnnouncementModalOpen, setIsAnnouncementModalOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [error, setError] = useState("");
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdatingLanguage, setIsUpdatingLanguage] = useState(false);
  const { language, t } = useUiText();

  const isImpersonating = Boolean(impersonation?.active);
  const controllingUser = impersonation?.real_user || user;
  const canOpenAdmin = Boolean(controllingUser?.staff);
  const canImpersonate = Boolean(controllingUser?.executive);
  const canCreateAnnouncement = Boolean(controllingUser?.executive);
  const menuItemClass =
    "block w-full px-4 text-left touch-manipulation min-h-[52px] py-3 text-base md:min-h-0 md:py-2 md:text-sm hover:bg-gray-50";

  useOutsideClick(portalRef, () => setIsMenuOpen(false));

  // Mount the menu, then flip it to the open transform on the next frame.
  useEffect(() => {
    if (isMenuOpen) {
      setIsMenuRendered(true);
      const frameId = requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsMenuShown(true));
      });
      return () => cancelAnimationFrame(frameId);
    }

    setIsMenuShown(false);
  }, [isMenuOpen]);

  // Fallback unmount if the close transition never fires (tab change, reduced motion).
  useEffect(() => {
    if (isMenuOpen || !isMenuRendered) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => setIsMenuRendered(false), 200);
    return () => window.clearTimeout(timeoutId);
  }, [isMenuOpen, isMenuRendered]);

  /**
   * Unmounts the portal after it finishes contracting into the initials button.
   *
   * @param {React.TransitionEvent<HTMLDivElement>} event Menu transition event.
   * @returns {void}
   */
  const handleMenuTransitionEnd = (event) => {
    if (event.target !== event.currentTarget || isMenuOpen) {
      return;
    }
    setIsMenuRendered(false);
  };

  const availableUsers = useMemo(() => {
    const query = search.toLowerCase();
    return users
      .filter((candidate) => !candidate.executive)
      .filter((candidate) => candidate.id !== controllingUser?.id)
      .filter((candidate) => {
        const searchableValue = [
          candidate.first,
          candidate.last,
          candidate.email,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return searchableValue.includes(query);
      });
  }, [controllingUser?.id, search, users]);

  /**
   * Opens the Django admin panel in a new browser tab.
   *
   * @returns {void}
   */
  const handleOpenAdmin = () => {
    openAdminPanel(baseURL);
    setIsMenuOpen(false);
  };

  /**
   * Persists the selected language and keeps the portal open.
   *
   * @param {string} nextLanguage Language code (`en` or `es`).
   * @returns {Promise<void>}
   */
  const handleLanguageChange = async (nextLanguage) => {
    if (nextLanguage === language || isUpdatingLanguage) {
      return;
    }

    setIsUpdatingLanguage(true);
    setError("");

    try {
      await updatePreferredLanguage(nextLanguage);
    } catch (updateError) {
      setError(t(UI_COPY.nav.languageUpdateFailed));
    } finally {
      setIsUpdatingLanguage(false);
    }
  };

  /**
   * Opens the executive create-announcement modal.
   *
   * @returns {void}
   */
  const handleOpenAnnouncementModal = () => {
    setIsMenuOpen(false);
    setError("");
    setIsAnnouncementModalOpen(true);
  };

  /**
   * Loads users and opens the impersonation picker.
   *
   * @returns {Promise<void>}
   */
  const handleOpenImpersonationModal = async () => {
    setIsMenuOpen(false);
    setIsImpersonationModalOpen(true);
    setSearch("");
    setSelectedUserId("");
    setError("");
    setIsLoadingUsers(true);

    try {
      const response = await getUsersByRole({ active: true });
      setUsers(response || []);
    } catch (loadError) {
      setError("Unable to load users for impersonation.");
    } finally {
      setIsLoadingUsers(false);
    }
  };

  /**
   * Starts impersonation for the selected user.
   *
   * @returns {Promise<void>}
   */
  const handleConfirmImpersonation = async () => {
    if (!selectedUserId) {
      setError("Select a user to impersonate.");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      await startImpersonation(Number(selectedUserId));
      await refreshUser();
      setIsImpersonationModalOpen(false);
    } catch (submitError) {
      setError(
        submitError.response?.data?.message || "Unable to start impersonation."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Ends the current impersonation session.
   *
   * @returns {Promise<void>}
   */
  const handleEndImpersonation = async () => {
    setIsSubmitting(true);
    setError("");

    try {
      await stopImpersonation();
      await refreshUser();
      setIsMenuOpen(false);
    } catch (submitError) {
      setError(
        submitError.response?.data?.message || "Unable to end impersonation."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative" ref={portalRef}>
      <button
        className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-white/10 text-sm font-bold text-white transition hover:bg-white/20"
        type="button"
        onClick={() => setIsMenuOpen((isOpen) => !isOpen)}
        title={t(UI_COPY.nav.userMenu)}
      >
        {getUserInitials(controllingUser)}
      </button>

      {isMenuRendered ? (
        <>
          <button
            className={`fixed inset-x-0 top-14 bottom-0 z-[55] bg-black/30 transition-opacity duration-150 ease-out md:hidden ${
              isMenuShown ? "opacity-100" : "opacity-0"
            }`}
            type="button"
            aria-label={t(UI_COPY.nav.closeMenu)}
            onClick={() => setIsMenuOpen(false)}
          />
          <div
            className={`fixed inset-x-0 top-14 z-[60] origin-top-right overflow-hidden border-b border-gray-200 bg-white py-3 text-left text-gray-700 shadow-xl transition-[transform,opacity] duration-150 ease-out will-change-transform md:absolute md:inset-x-auto md:right-0 md:top-12 md:w-96 md:rounded-lg md:border md:border-gray-200 md:py-2 md:text-sm ${
              isMenuShown
                ? "scale-100 opacity-100"
                : "scale-[0.2] opacity-0"
            }`}
            onTransitionEnd={handleMenuTransitionEnd}
          >
            <div className="border-b border-gray-100 px-4 py-4 md:py-3">
              <p className="text-lg font-semibold text-gray-900 md:text-sm">
                {getUserDisplayName(controllingUser)}
              </p>
              <p className="truncate text-sm text-gray-500 md:text-xs">
                {controllingUser?.email}
              </p>
            </div>

            <div className="border-b border-gray-100 px-4 py-4 md:py-3">
              <div className="md:flex md:items-center md:justify-between md:gap-3">
                <p className="text-sm font-semibold text-gray-500 md:text-xs">
                  {t(UI_COPY.nav.language)}
                </p>
                <div className="hidden md:block">
                  <DesktopLanguageToggle
                    language={language}
                    disabled={isUpdatingLanguage}
                    onChange={handleLanguageChange}
                    t={t}
                  />
                </div>
              </div>
              <div className="md:hidden">
                <MobileLanguageToggle
                  language={language}
                  disabled={isUpdatingLanguage}
                  onChange={handleLanguageChange}
                  t={t}
                />
              </div>
              {isImpersonating ? (
                <p className="mt-3 text-sm text-amber-700 md:mt-2 md:text-xs">
                  This changes their saved language, and is a permanent user setting change even during impersonation.
                </p>
              ) : null}
            </div>

            {error ? (
              <p className="mx-4 mt-3 rounded-md bg-red-50 px-3 py-3 text-sm text-red-700 md:mx-3 md:mt-2 md:py-2 md:text-xs">
                {error}
              </p>
            ) : null}

            {canOpenAdmin ? (
              <button
                className={menuItemClass}
                type="button"
                onClick={handleOpenAdmin}
              >
                {t(UI_COPY.nav.openAdminPanel)}
              </button>
            ) : null}

            {canCreateAnnouncement ? (
              <button
                className={menuItemClass}
                type="button"
                onClick={handleOpenAnnouncementModal}
              >
                Create Announcement
              </button>
            ) : null}

            {isImpersonating ? (
              <button
                className="mx-4 my-3 block min-h-[52px] w-[calc(100%-2rem)] touch-manipulation rounded-md bg-orange-800 px-3 py-3 text-left text-base font-semibold text-white hover:bg-orange-700 md:mx-3 md:my-2 md:min-h-0 md:w-[calc(100%-1.5rem)] md:py-2 md:text-sm"
                type="button"
                onClick={handleEndImpersonation}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Ending..." : "End Impersonation"}
              </button>
            ) : canImpersonate ? (
              <button
                className={menuItemClass}
                type="button"
                onClick={handleOpenImpersonationModal}
              >
                Begin User Impersonation
              </button>
            ) : null}

            <div className="mt-2 border-t border-gray-100 pt-2">
              <button
                className={`${menuItemClass} text-red-700 hover:bg-red-50`}
                type="button"
                onClick={() => {
                  setIsMenuOpen(false);
                  onLogout();
                }}
              >
                {t(UI_COPY.nav.logout)}
              </button>
            </div>
          </div>
        </>
      ) : null}

      <CreateAnnouncementModal
        isOpen={isAnnouncementModalOpen}
        onClose={() => setIsAnnouncementModalOpen(false)}
      />

      <ModalWrapper
        isOpen={isImpersonationModalOpen}
        onClose={() => setIsImpersonationModalOpen(false)}
        maxWidth={560}
      >
        <div className="p-6">
          <h2 className="pr-8 text-xl font-bold text-gray-900">
            Begin User Impersonation
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Select an active non-executive user to act on their behalf.
          </p>

          <label className="mt-6 block text-sm font-semibold text-gray-700">
            Search Users
            <input
              className="mt-2 block min-h-[42px] w-full rounded-md border border-gray-300 px-3 text-sm outline-none focus:border-secondary-400 focus:ring-4 focus:ring-secondary-100"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by name or email"
            />
          </label>

          <div className="mt-4 max-h-72 overflow-y-auto rounded-md border border-gray-200 min-h-[288px]">
            {isLoadingUsers ? (
              <p className="px-4 py-6 text-center text-sm text-gray-500">
                Loading users...
              </p>
            ) : availableUsers.length ? (
              availableUsers.map((candidate) => (
                <label
                  key={candidate.id}
                  className="flex cursor-pointer items-start gap-3 border-b border-gray-100 px-4 py-3 last:border-b-0 hover:bg-gray-50"
                >
                  <input
                    className="mt-1"
                    type="radio"
                    name="impersonation_user"
                    value={candidate.id}
                    checked={Number(selectedUserId) === candidate.id}
                    onChange={(event) => setSelectedUserId(event.target.value)}
                  />
                  <span>
                    <span className="block font-semibold text-gray-900">
                      {getUserDisplayName(candidate)}
                    </span>
                    <span className="block text-xs text-gray-500">
                      {candidate.email}
                    </span>
                  </span>
                </label>
              ))
            ) : (
              <p className="px-4 py-6 text-center text-sm text-gray-500">
                No matching users found.
              </p>
            )}
          </div>

          {error ? (
            <p className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </p>
          ) : null}

          <div className="mt-6 flex justify-end gap-3">
            <Button
              variant="cancel"
              text="Cancel"
              onClick={() => setIsImpersonationModalOpen(false)}
            />
            <Button
              variant={isSubmitting || !selectedUserId ? "disabled" : "primary"}
              text={isSubmitting ? "Starting..." : "Confirm"}
              onClick={handleConfirmImpersonation}
            />
          </div>
        </div>
      </ModalWrapper>
    </div>
  );
};

export default UserPortal;
